"use client";

import { BorderBeam } from "border-beam";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useTheme } from "next-themes";
import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useAuth } from "@/features/auth";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import { cn } from "@/lib/utils";

import {
  TOUR_MIN_VIEWPORT_WIDTH,
  TOURS,
  type TourPlacement,
  type TourStep,
} from "../lib/registry";
import { useToursStore } from "../lib/tour-store";

// Motion vocabulary aligned to transitions.dev tokens so the tour reads with
// the same rhythm as the rest of the platform.
//   Smooth ease out — modal/dropdown/panel + position/resize
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
//   Text-states/reveal easing
const EASE_IN_OUT = "easeInOut" as const;
// Durations (seconds)
const DUR_QUICK = 0.15; // modal/text-swap close
const DUR_FAST = 0.25; // modal/dropdown open, page slide
const DUR_RESIZE = 0.3; // card resize
const DUR_REVEAL = 0.5; // texts reveal, emphasis
// Texts-reveal step content (rise + blur, staggered)
const REVEAL_DISTANCE = 12;
const REVEAL_BLUR = 3;
const REVEAL_STAGGER = 0.04;
// Modal card entrance
const MODAL_SCALE = 0.96;

// Staggered "texts reveal": each line rises + de-blurs, held back by
// REVEAL_STAGGER so the eye lands on the title first. Exit is decoupled — a
// single quiet fade (no Y return, no blur) so dismissing doesn't reverse-play.
const revealContainer = {
  initial: {},
  animate: { transition: { staggerChildren: REVEAL_STAGGER } },
  // Lift the outgoing block out of flow so the incoming block alone drives the
  // card height, and fade every line together (no reverse stagger).
  exit: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    transition: { staggerChildren: 0 },
  },
};
const revealLine = {
  initial: {
    opacity: 0,
    y: REVEAL_DISTANCE,
    filter: `blur(${REVEAL_BLUR}px)`,
  },
  animate: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: DUR_REVEAL, ease: EASE },
  },
  exit: {
    opacity: 0,
    filter: "blur(0px)",
    transition: { duration: 0.2, ease: EASE_IN_OUT },
  },
};

type Rect = { top: number; left: number; width: number; height: number };

const SPOT_PAD = 8;
const CARD_GAP = 16;
const EDGE_PAD = 16;
const CARD_WIDTH = 400;

function findTarget(key: string | null): Element | null {
  if (!key) return null;
  return document.querySelector(`[data-tour="${key}"]`);
}

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

interface CardPos {
  top: number;
  left: number;
  /** Which side of the spotlight the card resolved to; null when centered. */
  side: TourPlacement | null;
}

function cardPosition(
  rect: Rect | null,
  placement: TourPlacement,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
): CardPos {
  if (!rect) {
    return {
      top: (viewport.height - card.height) / 2,
      left: (viewport.width - card.width) / 2,
      side: null,
    };
  }
  const spot = {
    top: rect.top - SPOT_PAD,
    left: rect.left - SPOT_PAD,
    width: rect.width + SPOT_PAD * 2,
    height: rect.height + SPOT_PAD * 2,
  };
  const centerX = spot.left + spot.width / 2 - card.width / 2;
  const centerY = spot.top + spot.height / 2 - card.height / 2;
  let top: number;
  let left: number;
  let side = placement;

  if (
    side === "right" &&
    spot.left + spot.width + CARD_GAP + card.width > viewport.width - EDGE_PAD
  )
    side = "left";
  else if (side === "left" && spot.left - CARD_GAP - card.width < EDGE_PAD)
    side = "right";
  else if (
    side === "bottom" &&
    spot.top + spot.height + CARD_GAP + card.height > viewport.height - EDGE_PAD
  )
    side = "top";
  else if (side === "top" && spot.top - CARD_GAP - card.height < EDGE_PAD)
    side = "bottom";
  switch (side) {
    case "right":
      left = spot.left + spot.width + CARD_GAP;
      top = centerY;
      break;
    case "left":
      left = spot.left - CARD_GAP - card.width;
      top = centerY;
      break;
    case "top":
      top = spot.top - CARD_GAP - card.height;
      left = centerX;
      break;
    default:
      top = spot.top + spot.height + CARD_GAP;
      left = centerX;
  }
  return {
    top: clamp(top, EDGE_PAD, viewport.height - card.height - EDGE_PAD),
    left: clamp(left, EDGE_PAD, viewport.width - card.width - EDGE_PAD),
    side,
  };
}

const CARET_SIZE = 12;

/**
 * Positions the tooltip caret on the card edge facing the spotlight, aligned
 * to the spotlight's center (clamped away from the rounded corners).
 */
function caretStyle(
  side: TourPlacement,
  spotCenter: { x: number; y: number },
  card: { top: number; left: number; width: number; height: number },
): CSSProperties {
  const half = CARET_SIZE / 2;
  const along =
    side === "left" || side === "right"
      ? clamp(spotCenter.y - card.top - half, 18, card.height - 18 - CARET_SIZE)
      : clamp(spotCenter.x - card.left - half, 18, card.width - 18 - CARET_SIZE);
  const base: CSSProperties = {
    width: CARET_SIZE,
    height: CARET_SIZE,
  };
  switch (side) {
    case "right": // card sits right of the spot → caret on card's left edge
      return { ...base, left: -half, top: along };
    case "left":
      return { ...base, right: -half, top: along };
    case "top": // card sits above the spot → caret on card's bottom edge
      return { ...base, bottom: -half, left: along };
    default:
      return { ...base, top: -half, left: along };
  }
}

function useIsMounted() {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
}

/**
 * True only on desktop-width viewports. Guided tours assume the desktop layout,
 * so the overlay stays hidden on mobile/tablet and reacts to live resizes.
 */
function useIsTourViewport() {
  const query = `(min-width: ${TOUR_MIN_VIEWPORT_WIDTH}px)`;
  return useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia(query);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/**
 * Global guided-tour overlay. Mount once in the dashboard layout; it renders
 * nothing until a tour is started via the tours store. Also hydrates the
 * per-user "seen" state so auto-start hooks can make their decision.
 */
export function TourOverlay() {
  const { user, isLoading } = useAuth();
  const hydrate = useToursStore((state) => state.hydrate);
  const activeTourId = useToursStore((state) => state.activeTourId);

  useEffect(() => {
    if (!FEATURE_FLAGS.tours || isLoading) return;
    hydrate(user?.id ?? null);
  }, [isLoading, user?.id, hydrate]);

  const mounted = useIsMounted();
  const isTourViewport = useIsTourViewport();
  if (!FEATURE_FLAGS.tours || !mounted || !isTourViewport) return null;

  return createPortal(
    <AnimatePresence>
      {activeTourId ? <TourRun key={activeTourId} /> : null}
    </AnimatePresence>,
    document.body,
  );
}

function TourRun() {
  const activeTourId = useToursStore((state) => state.activeTourId);
  const stepIndex = useToursStore((state) => state.step);
  const next = useToursStore((state) => state.next);
  const back = useToursStore((state) => state.back);
  const goTo = useToursStore((state) => state.goTo);
  const finish = useToursStore((state) => state.finish);
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";

  const tour = activeTourId ? TOURS[activeTourId] : null;

  // Steps whose target is missing on this screen are dropped at start so the
  // tour degrades gracefully (e.g. an empty table or hidden sidebar).
  const [steps] = useState<TourStep[]>(() =>
    (tour?.steps ?? []).filter(
      (step) => step.target === null || findTarget(step.target),
    ),
  );
  const step = steps[Math.min(stepIndex, steps.length - 1)] ?? null;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex >= steps.length - 1;

  const onNext = () => (isLast ? finish() : next());

  const targetKey = step?.target ?? null;
  useEffect(() => {
    const el = findTarget(targetKey);
    if (!el) return;
    el.scrollIntoView({
      behavior: "instant" as ScrollBehavior,
      block: "nearest",
      inline: "nearest",
    });
  }, [targetKey]);

  const [rect, setRect] = useState<Rect | null>(null);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const el = findTarget(targetKey);
      const box = el?.getBoundingClientRect();
      const nextRect =
        box && box.width > 0
          ? {
              top: box.top,
              left: box.left,
              width: box.width,
              height: box.height,
            }
          : null;
      setRect((prev) => (sameRect(prev, nextRect) ? prev : nextRect));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [targetKey]);

  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish();
      } else if (event.key === "ArrowRight" || event.key === "Enter") {
        event.preventDefault();
        onNext();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        back();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(220);
  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      setCardHeight(node.offsetHeight);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const cardWidth = Math.min(CARD_WIDTH, viewport.width - EDGE_PAD * 2);
  const cardPos = useMemo(
    () =>
      cardPosition(
        rect,
        step?.placement ?? "bottom",
        { width: cardWidth, height: cardHeight },
        viewport,
      ),
    [rect, step?.placement, cardWidth, cardHeight, viewport],
  );

  const spot: Rect = rect
    ? {
        top: rect.top - SPOT_PAD,
        left: rect.left - SPOT_PAD,
        width: rect.width + SPOT_PAD * 2,
        height: rect.height + SPOT_PAD * 2,
      }
    : {
        top: viewport.height / 2,
        left: viewport.width / 2,
        width: 0,
        height: 0,
      };

  if (!tour || !step) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[90]"
      role="dialog"
      aria-modal="true"
      aria-label={tour.label}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: DUR_QUICK, ease: EASE } }}
      transition={{ duration: DUR_FAST, ease: EASE }}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="Next step"
        onClick={onNext}
        className="absolute inset-0 cursor-default appearance-none border-0 bg-transparent p-0 outline-none"
      />

      {/* Backdrop dim + spotlight cutout via a giant box-shadow */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute rounded-2xl"
        initial={false}
        animate={{
          top: spot.top,
          left: spot.left,
          width: spot.width,
          height: spot.height,
        }}
        transition={{ type: "spring", duration: 0.55, bounce: 0.12 }}
        style={{
          boxShadow:
            "0 0 0 9999px rgba(9, 9, 11, 0.72), 0 0 44px 6px rgba(255, 175, 83, 0.30)",
        }}
      />

      {/* Pulsing spotlight ring */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute rounded-2xl border-[1.5px] border-ai/70"
        initial={false}
        animate={{
          top: spot.top,
          left: spot.left,
          width: spot.width,
          height: spot.height,
          opacity: rect ? [0.85, 0.35, 0.85] : 0,
        }}
        transition={{
          top: { type: "spring", duration: 0.55, bounce: 0.12 },
          left: { type: "spring", duration: 0.55, bounce: 0.12 },
          width: { type: "spring", duration: 0.55, bounce: 0.12 },
          height: { type: "spring", duration: 0.55, bounce: 0.12 },
          opacity: {
            duration: 2,
            ease: "easeInOut",
            repeat: Number.POSITIVE_INFINITY,
          },
        }}
      />

      {/* Step card */}
      <motion.div
        className="absolute"
        initial={false}
        animate={{
          top: cardPos.top,
          left: cardPos.left,
          width: cardWidth,
        }}
        transition={{ type: "spring", duration: 0.55, bounce: 0.14 }}
      >
        {/* Tooltip caret pointing at the spotlighted element. Painted before
            (under) the card so its inner half hides behind the opaque bg. */}
        {rect && cardPos.side ? (
          <div
            aria-hidden
            className="absolute rotate-45 rounded-[2px] border border-contrast/10 bg-surface-elevated"
            style={caretStyle(cardPos.side, {
              x: spot.left + spot.width / 2,
              y: spot.top + spot.height / 2,
            }, { ...cardPos, width: cardWidth, height: cardHeight })}
          />
        ) : null}

        <BorderBeam
          size="md"
          colorVariant="sunset"
          strength={isLight ? 1 : 0.7}
          hueRange={8}
          borderRadius={16}
          theme={isLight ? "light" : "dark"}
          style={{ "--beam-hue-base": "16deg" } as CSSProperties}
        >
          <motion.div
            className="relative overflow-hidden rounded-2xl border border-contrast/10 bg-surface-elevated shadow-[0_16px_44px_-14px_rgba(0,0,0,0.18)] dark:shadow-[0_24px_60px_-12px_rgba(0,0,0,0.45)]"
            style={{ transformOrigin: "center" }}
            initial={{ opacity: 0, scale: MODAL_SCALE }}
            animate={{ opacity: 1, scale: 1, height: cardHeight }}
            transition={{
              opacity: { duration: DUR_FAST, ease: EASE },
              scale: { duration: DUR_FAST, ease: EASE },
              height: { duration: DUR_RESIZE, ease: EASE },
            }}
          >
            <button
              type="button"
              onClick={finish}
              aria-label="Close tour"
              className="absolute top-3.5 right-3.5 z-[2] flex size-6 cursor-pointer items-center justify-center rounded-full text-contrast/40 transition-[color,background-color] duration-200 hover:bg-contrast/[0.07] hover:text-contrast/80"
            >
              <Icon name="IconCrossMedium" size={14} />
            </button>

            <div ref={contentRef} className="flex flex-col gap-4 p-5">
              <div className="relative">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={step.key}
                    variants={revealContainer}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="flex flex-col gap-2.5"
                  >
                    <motion.div
                      variants={revealLine}
                      className="flex items-center gap-2.5 pr-8"
                    >
                      <Icon
                        name={step.icon}
                        size={20}
                        className="shrink-0 text-ai"
                      />
                      <div className="flex min-w-0 flex-col gap-px">
                        <span className="text-[11px] font-medium tracking-[-0.1px] text-contrast/50">
                          {tour.label} · {stepIndex + 1} of {steps.length}
                        </span>
                        <h2 className="truncate text-base font-semibold tracking-[-0.3px] text-heading">
                          {step.title}
                        </h2>
                      </div>
                    </motion.div>
                    <motion.p
                      variants={revealLine}
                      className="text-[13px] leading-[1.55] text-contrast/65"
                    >
                      {step.body}
                    </motion.p>
                    {step.image ? (
                      <motion.div
                        variants={revealLine}
                        className="relative mt-1 aspect-[1024/543] w-full overflow-hidden rounded-xl border border-contrast/10 bg-contrast/4 shadow-[0_6px_18px_-10px_rgba(0,0,0,0.22)] dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)]"
                      >
                        <Image
                          src={step.image}
                          alt=""
                          fill
                          sizes="400px"
                          className="object-cover object-top"
                          priority
                        />
                      </motion.div>
                    ) : null}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="relative flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  {steps.map((dotStep, index) => (
                    <button
                      key={dotStep.key}
                      type="button"
                      onClick={() => goTo(index)}
                      aria-label={`Go to step ${index + 1}`}
                      className="flex cursor-pointer items-center justify-center p-0.5"
                    >
                      <motion.span
                        className={cn(
                          "block h-1.5 rounded-full",
                          index === stepIndex ? "bg-ai" : "bg-contrast/20",
                        )}
                        animate={{ width: index === stepIndex ? 16 : 6 }}
                        transition={{ duration: DUR_FAST, ease: EASE }}
                      />
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  {isFirst ? (
                    <Button
                      variant="secondary"
                      onClick={finish}
                      className="h-8 px-3.5 text-[13px]"
                    >
                      Skip
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={back}
                      className="h-8 px-3.5 text-[13px]"
                    >
                      Back
                    </Button>
                  )}
                  <motion.div whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="ai-solid"
                      onClick={onNext}
                      className="h-8 border-white/10 px-4 text-[13px] font-semibold text-[#171717] [background-image:linear-gradient(180deg,rgba(255,175,83,0)_0%,rgba(255,255,255,0.4)_100%),linear-gradient(90deg,#ffaf53_0%,#ffaf53_100%)] hover:brightness-[1.04]"
                    >
                      <span>
                        {isFirst ? "Start tour" : isLast ? "Got it!" : "Next"}
                      </span>
                      {!isLast ? <Icon name="IconArrowRight" size={14} /> : null}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </BorderBeam>
      </motion.div>
    </motion.div>
  );
}
